/*
 * @Author: Teemo Ding
 * @Date: 2022-03-01 15:30:03
 * @LastEditTime: 2022-06-10 14:53:05
 * @LastEditors: Json Chen
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCommunication/index.tsx
 */
import CommonHeader from '@/src/components/CommonHeader';
import ModalHOC from '@/src/constrains/ModalHOC';
import useBreadCrumbData from '@/src/pages/SalesCannelVersion/Hooks/useBreadCrumbData';
import {
  addSalesChannelCommunicationGateways,
  changePriority,
  changeSimultaneous,
  deleteCommunicationGateway,
  getCommunicationSettings,
} from '@/src/request/salesChannel/setting/communication';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Spin, message } from 'antd';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSalesChannelType } from 'src/constrains/SalesChannelHOC/useSalesChannel';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { ChannelType } from 'src/types/common/SalesChannel';
import { SalesChannelModeAccessControlHOC } from 'src/constrains/SalesChannelModeAccessControl';
import {
  CommunicationChannelType,
  CommunicationChannelTypeMp,
} from 'src/types/common/communication';
import CommunicationChannelsPriority from './CommunicationChannelsPriority';
import CommunicationGateways from './CommunicationGateways';
import CommunicationTemplates from './CommunicationTemplates';
import SelectCommunicationGatewayModal from './Components/SelectCommunicationGatewayModal';
import { default as styles } from './css.module.less';

const permissions = (uuid: string) => {
  const view_communication_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCommunication,
        operation: EnumOperation.view_communication_settings,
        uuid,
      }),
    ],
  });
  const edit_communication_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCommunication,
        operation: EnumOperation.edit_communication_setting,
        uuid,
      }),
    ],
  });

  return {
    view_communication_settings,
    edit_communication_setting,
  };
};

const SalesChannelCommunication = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { modalOp } = props;
  const { setShowModal, setModalTitle, setModalContent } = modalOp;
  const channelType = useSalesChannelType();
  const isMini = channelType == ChannelType.MiniProgram;
  const [loading, setLoading] = useState(false);
  const params: any = useParams();
  const history = useHistory();
  const { uuid: salesChannelUuid } = params;

  const { edit_communication_setting, view_communication_settings } =
    permissions(salesChannelUuid);

  const { loading: breadCrumbLoading, breadcrumbData } = useBreadCrumbData({
    lastSecond: getI18n('general.sider.settings'),
    lastOne: getI18n(
      'salesChannelCommunication.communicationCategory.communication',
    ),
  });

  const {
    data: {
      gateways = [],
      salesChannelCommunicationSetting: {
        groupedGateways = [],
        simultaneous = false,
        priorities = [],
        groupedTemplates = [],
        uuid: communicationUuid = '',
      } = {},
    } = {} as any,
    run: _getCommunicationSettings,
  } = useRequest(() => getCommunicationSettings(salesChannelUuid), {
    ready: !!view_communication_settings,
  });

  // const groupedGatewaysUuids = (groupedGateways || []).flatMap((item) =>
  //   (item.gateways || []).map((list) => list.uuid),
  // );

  const { run: _addSalesChannelCommunicationGateways } = useRequest(
    (data) => addSalesChannelCommunicationGateways(data),
    {
      manual: true,
      ready: !!edit_communication_setting,
      onSuccess: () => {
        setLoading(false);
        _getCommunicationSettings();
      },
    },
  );

  const { run: _deleteCommunicationGateway } = useRequest(
    (gatewayUuid) => deleteCommunicationGateway(salesChannelUuid, gatewayUuid),
    {
      manual: true,
      ready: !!edit_communication_setting,
      onSuccess: () => {
        message.success(getI18n('general.deletedsuccessfully'));
        _getCommunicationSettings();
      },
    },
  );

  const { run: _changeSimultaneous } = useRequest(
    (value) => changeSimultaneous(salesChannelUuid, value),
    {
      manual: true,
      ready: !!edit_communication_setting,
      onSuccess: () => {
        _getCommunicationSettings();
      },
    },
  );
  const { run: _changePriority } = useRequest(
    (data) => changePriority(salesChannelUuid, data),
    {
      manual: true,
      ready: !!edit_communication_setting,
      onSuccess: () => {
        _getCommunicationSettings();
      },
    },
  );

  const translateGateways = (k) =>
    k.reduce((acc, cur) => {
      const accIndex = acc.findIndex((o) => o.channelType == cur.channelType);
      if (accIndex > -1) {
        acc[accIndex].gatewayList.push({
          name: cur.name,
          gatewayProvider: cur.gatewayProvider,
          uuid: cur.uuid,
        });
      } else {
        acc.push({
          channelType: cur.channelType,
          channelName: cur.channelType,
          gatewayList: [
            {
              name: cur.name,
              gatewayProvider: cur.gatewayProvider,
              uuid: cur.uuid,
            },
          ],
        });
      }
      return acc;
    }, []);

  const gatewaysList = translateGateways(gateways);
  const gatewaysAllListWidthMp = gatewaysList.concat([
    {
      channelType: CommunicationChannelTypeMp.WechatMP,
      channelName: getI18n(
        'salesChannelCommunication.communicationCategory.miniProgramSubscribeMessage',
      ),
    },
  ]);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const onGoBackClick = () => {
    history.push(`/salesChannel/settings/${salesChannelUuid}`);
  };
  const handleAddGateway = (value, check, radioValue) => {
    console.log(value, check);
    _addSalesChannelCommunicationGateways({
      salesChannelUuid,
      channelType: value.channelType,
      gatewayUuids:
        (value || {}).channelType === CommunicationChannelType.LiveChat
          ? [radioValue]
          : check,
    });
    handleCloseModal();
  };

  const handleSelectCommunicationGateway = () => {
    setShowModal(true);
    setModalTitle(
      getI18n('salesChannelCommunication.index.selectCommunicationGateway'),
    );
    setModalContent(
      <SelectCommunicationGatewayModal
        groupedGateways={groupedGateways}
        handleCloseModal={handleCloseModal}
        notFoundContent={getI18n(
          'salesChannelCommunication.communicationCategory.notFoundContent',
        )}
        options={isMini ? gatewaysAllListWidthMp : gatewaysList}
        onConfirm={handleAddGateway}
      />,
    );
  };

  const handleEditTemplate = (category) => {
    console.log(category);
    history.push(
      `/salesChannel/setting/communication/template/${salesChannelUuid}/${communicationUuid}/${category}`,
    );
  };

  return (
    <Spin spinning={loading || breadCrumbLoading}>
      <CommonHeader
        breadcrumbData={breadcrumbData}
        onGoBackClick={onGoBackClick}
      />
      <div className={styles.commonContainer}>
        <CommunicationGateways
          data={groupedGateways}
          deleteFun={_deleteCommunicationGateway}
          onSelectCommunicationGateway={handleSelectCommunicationGateway}
          disableDelete={!edit_communication_setting}
          disableEdit={!edit_communication_setting}
        />
        <CommunicationChannelsPriority
          priorities={priorities}
          changePriorities={_changePriority}
          simultaneous={simultaneous}
          changeSimultaneous={_changeSimultaneous}
          disableEdit={!edit_communication_setting}
        />
        <CommunicationTemplates
          priorities={priorities}
          groupedTemplates={groupedTemplates}
          editFun={handleEditTemplate}
          disableEdit={!edit_communication_setting}
        />
      </div>
    </Spin>
  );
};

export default SalesChannelModeAccessControlHOC(
  ModalHOC(SalesChannelCommunication),
);
